import React, { useEffect, useState } from "react";
import {
  Card,
  Input,
  Tag,
  Space,
  Spin,
  Avatar,
  Dropdown,
  Button,
  Empty,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const statusColors = {
  OPEN: { bg: "#e6f7ff", color: "#1890ff" },
  IN_PROGRESS: { bg: "#fffbe6", color: "#fa8c16" },
  RESOLVED: { bg: "#f6ffed", color: "#52c41a" },
  CLOSED: { bg: "#fff1f0", color: "#f5222d" },
};

export const getInitials = (user) => {
  const first = user?.firstName?.[0] || "";
  const last = user?.lastName?.[0] || "";
  return (first + last).toUpperCase() || "?";
};

const categoryMap = {
  "General Inquiry": "GENERAL_INQUIRY",
  "Technical Support": "TECHNICAL_SUPPORT",
  "Billing Issue": "BILLING_ISSUE",
  Feedback: "FEEDBACK",
  "Account Help": "ACCOUNT_HELP",
  Other: "OTHER",
};

export default function TicketList({
  tickets,
  loading,
  selected,
  onSelect,
  onSearch,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const filterMenuItems = [
    { key: "ALL", label: "All Tickets" },
    { type: "divider" },
    { key: "OPEN", label: "Open" },
    { key: "IN_PROGRESS", label: "In Progress" },
    { key: "RESOLVED", label: "Resolved" },
    { key: "CLOSED", label: "Closed" },
  ];

  const menuProps = {
    items: filterMenuItems,
    onClick: ({ key }) => setStatusFilter(key),
  };

  // initial search or reset on search/filter change
  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setInitialLoading(true);
    onSearch({
      keyword: search || undefined,
      status: statusFilter === "ALL" ? undefined : statusFilter,
      category: categoryFilter || undefined,
      page: 0,
      size: 20,
    }).finally(() => setInitialLoading(false));
  }, [search, statusFilter, categoryFilter]);

  const fetchNext = () => {
    const nextPage = page + 1;
    setLoadingMore(true);

    onSearch({
      keyword: search || undefined,
      status: statusFilter === "ALL" ? undefined : statusFilter,
      page: nextPage,
      size: 20,
    })
      .then((newTickets) => {
        if (!newTickets || newTickets.length < 20) setHasMore(false);
        setPage(nextPage);
      })
      .finally(() => setLoadingMore(false));
  };

  return (
    <Card
      style={{
        borderRadius: 8,
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        paddingBottom: 0,
      }}
    >
      <p style={{ fontFamily: "Inter", fontWeight: 600, marginBottom: 15 }}>
        Help Requests
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Input.Search
          placeholder="Search"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <Dropdown
          menu={{
            ...menuProps,
            style: { fontFamily: "'Inter', sans-serif", fontSize: 12 },
          }}
          trigger={["click"]}
          popupRender={(menu) => (
            <div style={{ width: 200 }}>{React.cloneElement(menu)}</div>
          )}
        >
          <Button
            icon={<FilterOutlined style={{ color: "#1890ff" }} />}
            style={{ borderRadius: 6 }}
          />
        </Dropdown>
      </div>

      <div
        id="scrollableDiv"
        style={{
          height: "90vh",
          overflow: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="no-scrollbar"
      >
        {initialLoading && page === 0 ? (
          <div style={{ textAlign: "center" }}>
            <Spin />
          </div>
        ) : tickets.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No tickets found"
            style={{ marginTop: 50 }}
          />
        ) : (
          <InfiniteScroll
            dataLength={tickets.length}
            next={fetchNext}
            hasMore={hasMore && tickets.length > 0}
            loader={
              loadingMore && (
                <div style={{ textAlign: "center", margin: 12 }}>
                  <Spin size="small" />
                </div>
              )
            }
            scrollableTarget="scrollableDiv"
            style={{ display: "flex", flexDirection: "column", padding: 3 }}
          >
            {tickets.map((item) => (
              <div
                key={item.id}
                onClick={() => onSelect(item)}
                style={{
                  cursor: "pointer",
                  border:
                    selected?.id === item.id
                      ? "1.5px solid #1890ff"
                      : "1px solid transparent",
                  borderRadius: 6,
                  padding: 16,
                  marginBottom: 12,
                  background: "white",
                  transition: "all 0.3s",
                  fontFamily: "'Inter', sans-serif",
                  boxShadow:
                    "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <Avatar
                    size="small"
                    style={{
                      backgroundColor: "#fde3cf",
                      color: "#f56a00",
                      fontFamily: "Inter",
                    }}
                  >
                    {getInitials(item.user)}
                  </Avatar>
                  <span style={{ fontWeight: 600, flex: 1, marginLeft: 10 }}>
                    {item.user?.firstName} {item.user?.lastName}
                  </span>
                  <span style={{ fontSize: 11, color: "#999" }}>
                    {item.createdAt ? dayjs(item.createdAt).fromNow() : ""}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 12,
                    lineHeight: 1.4,
                  }}
                >
                  {item.message?.slice(0, 60)}...
                </div>
                <Space size={4}>
                  {item.status && (
                    <Tag
                      style={{
                        borderRadius: 3,
                        fontFamily: "Inter",
                        backgroundColor:
                          statusColors[item.status]?.bg || "#f0f0f0",
                        color: statusColors[item.status]?.color || "#000",
                        fontSize: 11,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();

                        // toggle
                        const newStatusFilter =
                          item.status === statusFilter ? "ALL" : item.status;

                        setPage(0);
                        setHasMore(true);
                        setStatusFilter(newStatusFilter);

                        await onSearch({
                          keyword: search || undefined,
                          status:
                            newStatusFilter === "ALL"
                              ? undefined
                              : newStatusFilter,
                          category: categoryFilter || undefined,
                          page: 0,
                          size: 20,
                        });
                      }}
                    >
                      {item.status}
                    </Tag>
                  )}
                  {item.category && (
                    <Tag
                      style={{
                        borderRadius: 3,
                        backgroundColor: "#f0f0f0",
                        color: "#595959",
                        fontFamily: "Inter",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();

                        const categoryKey =
                          categoryMap[item.category] || item.category;

                        // toggle filter
                        const newCategoryFilter =
                          categoryKey === categoryFilter ? null : categoryKey;

                        setPage(0);
                        setHasMore(true);
                        setCategoryFilter(newCategoryFilter);
                        setStatusFilter("ALL");

                        await onSearch({
                          category: newCategoryFilter || undefined,
                          page: 0,
                          size: 20,
                        });
                      }}
                    >
                      {item.category}
                    </Tag>
                  )}
                </Space>
              </div>
            ))}
          </InfiniteScroll>
        )}
      </div>

      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
    </Card>
  );
}
